import {useEffect, useState, useRef, useCallback} from 'react';
import {Button} from '../../Utility/Button';
import {RootState, useAppSelector} from '../../../store';
import {userService} from '../../../Services/userService';
import {format} from 'date-fns';

function UserSyncButton() {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [duration, setDuration] = useState<number | null>(null);
    const [prevExecution, setPrevExecution] = useState<{
        startDate: string;
        endDate: string;
        duration?: number;
    } | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [shouldPoll, setShouldPoll] = useState(false);

    const checkSyncStatus = useCallback(async () => {
        try {
            const response = await userService.getSyncStatus();
            const nowSyncing = response.data.isRunning;
            setIsSyncing(nowSyncing);

            setEndDate(response.data.endDate ?? null);
            setDuration(response.data.duration ?? null);
            setPrevExecution(response.data.prev ?? null);

            // Stop polling if sync is no longer running
            if (!nowSyncing) {
                setShouldPoll(false);
            }
        } catch (error) {
            console.error('Failed to check sync status:', error);
        }
    }, []);

    // Polling effect - only active when shouldPoll is true
    useEffect(() => {
        if (shouldPoll) {
            // Initial check
            checkSyncStatus();
            
            // Set up interval
            intervalRef.current = setInterval(checkSyncStatus, 15000);
        } else {
            // Clear interval when shouldPoll is false
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [shouldPoll, checkSyncStatus]);

    const handleSyncClick = useCallback(async () => {
        if (isLoading || isSyncing) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await userService.syncUsers();
            if (response.data.success) {
                setIsSyncing(true);
                // Start polling when sync starts
                setShouldPoll(true);
            }
        } catch (error) {
            console.error('Failed to start sync:', error);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, isSyncing]);

    const handleMouseOver = useCallback(() => {
        // Fetch status on mouseover
        checkSyncStatus();
    }, [checkSyncStatus]);

    const formatLastSyncDate = useCallback((): string => {
        const dateToUse = isSyncing && prevExecution ? prevExecution.endDate : endDate;

        if (!dateToUse) {
            return i18n.translate({id: 'users.sync.never', sourceName: 'Users', default: 'Never'});
        }
        return format(new Date(dateToUse), 'HH:mm:ss dd.MM.yyyy')
    }, [isSyncing, prevExecution, endDate, i18n]);

    const getDisplayDuration = useCallback((): number | null => {
        if (isSyncing && prevExecution && prevExecution.duration !== undefined) {
            return prevExecution.duration;
        }
        return duration;
    }, [isSyncing, prevExecution, duration]);

    const formatDuration = useCallback((seconds: number | null): string => {
        if (seconds === null) {
            return '';
        }

        if (seconds < 60) {
            return `${seconds}s`;
        } else if (seconds < 3600) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
    }, []);

    const getTooltipText = useCallback((): string => {
        const dateText = formatLastSyncDate();
        const displayDuration = getDisplayDuration();
        const durationText = displayDuration !== null && displayDuration > 0 ? ` (${formatDuration(displayDuration)})` : '';
        
        return i18n.translate({
            id: 'users.sync.lastSync',
            sourceName: 'Users',
            params: {date: dateText},
        }) + durationText;
    }, [formatLastSyncDate, getDisplayDuration, formatDuration, i18n]);

    return (
        <div className="flex flex-col items-end gap-2">
            <Button 
                variant="primary" 
                onClick={handleSyncClick} 
                onMouseOver={handleMouseOver}
                disabled={isLoading || isSyncing}
                tooltipText={getTooltipText()}
            >
                {isSyncing
                    ? i18n.translate({id: 'users.sync.syncing', sourceName: 'Users'})
                    : i18n.translate({id: 'users.sync.now', sourceName: 'Users'})}
            </Button>
        </div>
    );
}

export default UserSyncButton;
